import React, { useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

const SORT_OPTIONS = [
  { value: 'scans', label: 'Scans' },
  { value: 'posts', label: 'Posts' },
  { value: 'favorites', label: 'Favorites' },
  { value: 'rating', label: 'Rating' },
];

const PREFECTURES = [
  '', '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

export default function StoreAnalytics() {
  useAuthGuard();

  const [sortBy, setSortBy] = useState('scans');
  const [prefecture, setPrefecture] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/analytics/stores`);
        url.searchParams.set('sort', sortBy);
        url.searchParams.set('limit', '50');
        if (prefecture) url.searchParams.set('prefecture', prefecture);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRows(Array.isArray(data.data) ? data.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [sortBy, prefecture]);

  const fmt = (n) => new Intl.NumberFormat().format(n ?? 0);

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">Store Analytics</h2>
        <p className="page-subtitle">Store performance ranking</p>
      </div>

      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ maxWidth: '140px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>Sort: {o.label}</option>
              ))}
            </select>
            <select
              className="form-select"
              value={prefecture}
              onChange={(e) => setPrefecture(e.target.value)}
              style={{ maxWidth: '140px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              <option value="">All Prefectures</option>
              {PREFECTURES.filter(Boolean).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {prefecture && (
              <button className="btn btn-phoenix-secondary btn-sm" onClick={() => setPrefecture('')}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Store</th>
                <th>Prefecture</th>
                <th className="text-end">Scans</th>
                <th className="text-end">Posts</th>
                <th className="text-end">Favorites</th>
                <th className="text-end">Rating</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-4">Loading...</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={7} className="text-danger py-4">{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-4" style={{ color: '#8a94ad' }}>No data</td></tr>
              )}
              {!loading && !error && rows.map((row, i) => (
                <tr key={row.storeId || i}>
                  <td className="text-muted">{i + 1}</td>
                  <td className="fw-medium">{row.storeName || '-'}</td>
                  <td className="small">{row.prefecture || '-'}</td>
                  <td className="text-end">{fmt(row.scanCount)}</td>
                  <td className="text-end">{fmt(row.postCount)}</td>
                  <td className="text-end">{fmt(row.favoriteCount)}</td>
                  <td className="text-end">{(row.rating ?? 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
